'use client';

import cx from 'classix';
import { useTranslations } from 'next-intl';
import {
  FaBullseye,
  FaCircleCheck,
  FaEye,
  FaUserShield,
} from 'react-icons/fa6';

import rootPageSharedStyles from '@/app/[locale]/root-page-share.module.scss';
import useAppFlyerLink from '@/features/roomster-app/hooks/use-app-flyer-link';
import { ROUTER_LINKS } from '@/shared/constants/links-constants';
import Button from '@/ui/button/button';

import styles from './safety-section.module.scss';
import {setCookie} from "cookies-next/client";
import {SECONDS} from "@/shared/constants/time-constants";

const SAFETY_FEATURES = [
  {
    icon: <FaEye />,
    textToken: 'real-review',
  },
  {
    icon: <FaCircleCheck />,
    textToken: 'clear-badges',
  },
  {
    icon: <FaUserShield />,
    textToken: 'safer-contact',
  },
  {
    icon: <FaBullseye />,
    textToken: 'fewer-leads',
  },
] as const;

const PROCESS_FLOW = [
  'confirm-identity',
  'smart-check',
  'human-review',
] as const;

export default function SafetySection() {
  const listPlaceAppFlyerLink = useAppFlyerLink('mobile', 'mobileListPlace', ROUTER_LINKS.phoneAuth);
  const findPlaceAppFlyerLink = useAppFlyerLink('mobile', 'mobileFindPlace', ROUTER_LINKS.phoneAuth);
  const safetySectionTranslates = useTranslations('home.safety-section');
  const createListingTranslates = useTranslations('listing.create');

  const handleListPlaceAuth = () => {
    setCookie(
      'authData',
      JSON.stringify({ token: 'roommate-finder-list-room' }),
      { maxAge: SECONDS.HOUR, path: '/' },
    );
  };

  const handleFindPlaceAuth = () => {
    setCookie(
      'authData',
      JSON.stringify({ token: 'roommate-finder-find-room' }),
      { maxAge: SECONDS.HOUR, path: '/' },
    );
  };

  return (
    <section className={cx(rootPageSharedStyles.section, styles.safetySection)}>
      <div className={styles.safetySection_main}>
        <header className={rootPageSharedStyles.header}>
          <h2 className={rootPageSharedStyles.title}>
            {safetySectionTranslates('title')}
          </h2>
        </header>

        <p className={styles.safetySection_description}>
          {safetySectionTranslates('description')}
        </p>

        <ul className={styles.safetySection_featuresList}>
          {SAFETY_FEATURES.map((item) => (
            <li key={item.textToken} className={styles.safetySection_featureItem}>
              <div className={styles.safetySection_featureIcon}>
                {item.icon}
              </div>
              <p>
                {safetySectionTranslates.rich(item.textToken, { important: (chunks) => (<b>{chunks}</b>) })}
              </p>
            </li>
          ))}
        </ul>
      </div>

      <div className={styles.safetySection_process}>
        <h3 className={styles.safetySection_processTitle}>
          {safetySectionTranslates('validation-works')}
        </h3>

        <ul className={styles.safetySection_processList}>
          {PROCESS_FLOW.map((item, index) => (
            <li key={item} className={styles.safetySection_processItem}>
              <div className={styles.safetySection_processMarker}>
                {index + 1}
              </div>
              <p>
                {safetySectionTranslates.rich(item, { important: (chunks) => (<b>{chunks}</b>) })}
              </p>
            </li>
          ))}
        </ul>

        <div className={styles.safetySection_processActions}>
          <Button
            element="link"
            href={listPlaceAppFlyerLink}
            onNavigate={handleListPlaceAuth}
          >
            {createListingTranslates('list-a-room')}
          </Button>

          <Button
            element="link"
            href={findPlaceAppFlyerLink}
            onNavigate={handleFindPlaceAuth}
          >
            {createListingTranslates('find-a-room')}
          </Button>
        </div>
      </div>
    </section>
  );
}
